import React, { useState, useRef } from 'react';
import { 
  Mail, Phone, MapPin, Building, Globe, Award, Languages, Clock,
  Plus, X, Trash2, Loader2, Camera, Image as ImageIcon, Save,
  CheckCircle, AlertCircle, MessageCircle, ExternalLink, Briefcase
} from 'lucide-react';
import { ProvidersService } from '../../services/providerService';

export const ProviderSettings = ({ provider, onSave }) => {
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const portfolioInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: provider.name || '',
    email: provider.email || '',
    phone: provider.phone || '',
    location: provider.location || '',
    bio: provider.bio || '',
    avatarUrl: provider.avatarUrl || '',
    coverUrl: provider.coverUrl || '',
    portfolioUrl: provider.portfolioUrl || '',
    yearsExperience: provider.yearsExperience || '',
    responseTime: provider.responseTime || '',
  });

  // Array fields
  const [badges, setBadges] = useState(provider.badges || []);
  const [languages, setLanguages] = useState(provider.languages || []);
  const [certifications, setCertifications] = useState(provider.certifications || []);
  const [portfolioItems, setPortfolioItems] = useState(provider.portfolioItems || []);

  // New item inputs
  const [newBadge, setNewBadge] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newCertification, setNewCertification] = useState('');

  // Image previews
  const [avatarPreview, setAvatarPreview] = useState(provider.avatarUrl || '');
  const [coverPreview, setCoverPreview] = useState(provider.coverUrl || '');
  const [avatarFile, setAvatarFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  // Portfolio item form
  const [newPortfolioItem, setNewPortfolioItem] = useState({
    title: '',
    linkUrl: '',
    category: '',
  });
  const [portfolioImageFile, setPortfolioImageFile] = useState(null);
  const [portfolioImagePreview, setPortfolioImagePreview] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  // Avatar handling
  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, avatar: 'Image must be less than 5MB' }));
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, avatar: null }));
    }
  };

  // Cover handling
  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, cover: 'Image must be less than 5MB' }));
        return;
      }
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, cover: null }));
    }
  };

  // Array field handlers
  const addBadge = () => {
    const badge = newBadge.trim();
    if (badge && !badges.includes(badge) && badges.length < 10) {
      setBadges([...badges, badge]);
      setNewBadge('');
    }
  };

  const removeBadge = (badge) => setBadges(badges.filter(b => b !== badge));

  const addLanguage = () => {
    const lang = newLanguage.trim();
    if (lang && !languages.includes(lang) && languages.length < 10) {
      setLanguages([...languages, lang]);
      setNewLanguage('');
    }
  };

  const removeLanguage = (lang) => setLanguages(languages.filter(l => l !== lang));

  const addCertification = () => {
    const cert = newCertification.trim();
    if (cert && !certifications.includes(cert) && certifications.length < 10) {
      setCertifications([...certifications, cert]);
      setNewCertification('');
    }
  };

  const removeCertification = (cert) => setCertifications(certifications.filter(c => c !== cert));

  // Portfolio item handlers
  const handlePortfolioImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, portfolioImage: 'Image must be less than 5MB' }));
        return;
      }
      setPortfolioImageFile(file);
      setPortfolioImagePreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, portfolioImage: null }));
    }
  };

  const addPortfolioItem = async () => {
    if (!newPortfolioItem.title.trim() || !portfolioImageFile) {
      setErrors(prev => ({ ...prev, portfolio: 'Title and image are required' }));
      return;
    }

    try {
      const newItem = await ProvidersService.uploadPortfolioItem(
        provider.id,
        portfolioImageFile,
        {
          title: newPortfolioItem.title.trim(),
          linkUrl: newPortfolioItem.linkUrl.trim(),
          category: newPortfolioItem.category.trim(),
        }
      );
      setPortfolioItems([...portfolioItems, newItem]);
      setNewPortfolioItem({ title: '', linkUrl: '', category: '' });
      setPortfolioImageFile(null);
      setPortfolioImagePreview('');
      setErrors(prev => ({ ...prev, portfolio: null }));
    } catch (error) {
      setErrors(prev => ({ ...prev, portfolio: 'Failed to upload portfolio item' }));
    }
  };

  const removePortfolioItem = (itemId) => {
    setPortfolioItems(portfolioItems.filter(item => item.id !== itemId));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.bio && formData.bio.length > 1000) newErrors.bio = 'Bio must be less than 1000 characters';
    if (formData.portfolioUrl && !isValidUrl(formData.portfolioUrl)) {
      newErrors.portfolioUrl = 'Please enter a valid URL';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);
    setSaveSuccess(false);

    try {
      let avatarUrl = provider.avatarUrl || '';
      let coverUrl = provider.coverUrl || '';

      if (avatarFile) {
        avatarUrl = await ProvidersService.uploadAvatar(provider.id, avatarFile);
      }

      if (coverFile) {
        coverUrl = await ProvidersService.uploadCover(provider.id, coverFile);
      }

      const updateData = {
        name: formData.name.trim(),
        email: formData.email.trim() || '',
        phone: formData.phone.trim() || '',
        location: formData.location.trim() || '',
        bio: formData.bio.trim() || '',
        portfolioUrl: formData.portfolioUrl.trim() || '',
        yearsExperience: formData.yearsExperience ? parseInt(formData.yearsExperience) : 0,
        responseTime: formData.responseTime.trim() || '',
        badges: badges || [],
        languages: languages || [],
        certifications: certifications || [],
        portfolioItems: portfolioItems || [],
        avatarUrl: avatarUrl || '',
        coverUrl: coverUrl || '',
      };

      await ProvidersService.update(provider.id, updateData);
      setSaveSuccess(true);
      onSave?.({ ...provider, ...updateData });

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to save profile. Please try again.' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in-up pb-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Profile Settings</h1>
          <p className="text-slate-500 mt-1">Manage your public profile and business information</p>
        </div>
        {saveSuccess && (
          <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-xl">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Saved successfully!</span>
          </div>
        )}
      </div>

      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{errors.submit}</span>
        </div>
      )}

      {/* Cover & Avatar Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="relative h-48 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">
          {coverPreview && (
            <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-black/20" />
          <button
            type="button"
            onClick={() => coverInputRef.current?.click()}
            className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm text-slate-700 rounded-lg text-sm font-medium hover:bg-white transition-colors shadow-sm"
          >
            <Camera className="w-4 h-4" />
            Change Cover
          </button>
          <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverChange} className="hidden" />
        </div>
        {errors.cover && <p className="text-red-500 text-xs px-6 pt-2">{errors.cover}</p>}

        <div className="px-8 pb-8">
          <div className="flex flex-col md:flex-row items-start md:items-end gap-6 -mt-16 relative z-10">
            <div className="relative group">
              <div className="w-32 h-32 rounded-2xl ring-4 ring-white shadow-lg overflow-hidden bg-slate-100">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-indigo-100">
                    <span className="text-4xl font-bold text-indigo-600">{formData.name?.charAt(0) || '?'}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"
              >
                <Camera className="w-8 h-8 text-white" />
              </button>
              <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
            </div>
            {errors.avatar && <p className="text-red-500 text-xs">{errors.avatar}</p>}
            <div className="flex-1 pt-4 md:pt-0">
              <p className="text-sm text-slate-500 mb-1">Profile Photo</p>
              <p className="text-xs text-slate-400">Recommended: Square image, at least 200x200px</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><Phone className="w-5 h-5" /></div>
          <h3 className="font-bold text-slate-900 text-lg">Contact Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Display Name *</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange}
              className={`w-full px-4 py-2.5 border rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all ${errors.name ? 'border-red-300' : 'border-slate-200'}`} />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Email Address</label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com"
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Phone Number</label>
            <div className="relative">
              <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 (555) 000-0000"
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Location</label>
            <div className="relative">
              <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="City, Country"
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all" />
            </div>
          </div>
        </div>
      </div>

      {/* Professional Profile */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg"><Briefcase className="w-5 h-5" /></div>
          <h3 className="font-bold text-slate-900 text-lg">Professional Profile</h3>
        </div>
        <div className="space-y-6">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Bio</label>
            <textarea name="bio" value={formData.bio} onChange={handleChange} rows={4} maxLength={1000}
              placeholder="Tell clients about yourself, your expertise, and what makes you unique..."
              className={`w-full px-4 py-2.5 border rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all resize-none ${errors.bio ? 'border-red-300' : 'border-slate-200'}`} />
            <div className="flex justify-between mt-1">
              {errors.bio && <p className="text-red-500 text-xs">{errors.bio}</p>}
              <p className="text-slate-400 text-xs ml-auto">{formData.bio.length}/1000</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Portfolio URL</label>
              <div className="relative">
                <Globe className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input type="url" name="portfolioUrl" value={formData.portfolioUrl} onChange={handleChange} placeholder="https://yourportfolio.com"
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all ${errors.portfolioUrl ? 'border-red-300' : 'border-slate-200'}`} />
              </div>
              {errors.portfolioUrl && <p className="text-red-500 text-xs mt-1">{errors.portfolioUrl}</p>}
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Years Experience</label>
              <div className="relative">
                <Clock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input type="number" name="yearsExperience" value={formData.yearsExperience} onChange={handleChange} min="0" max="50" placeholder="e.g., 5"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Avg. Response Time</label>
              <div className="relative">
                <MessageCircle className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input type="text" name="responseTime" value={formData.responseTime} onChange={handleChange} placeholder="e.g., 1 hour, 24 hours"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skills, Languages & Certifications */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Skills/Badges */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 text-purple-600 rounded-lg"><Award className="w-5 h-5" /></div>
            <h3 className="font-bold text-slate-900">Skills & Badges</h3>
          </div>
          <div className="flex flex-wrap gap-2 mb-4 min-h-[40px]">
            {badges.map(badge => (
              <span key={badge} className="pl-3 pr-2 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-bold flex items-center gap-1 border border-indigo-100">
                {badge}
                <button onClick={() => removeBadge(badge)} className="hover:text-red-600 p-0.5"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {badges.length === 0 && <span className="text-xs text-slate-400 italic">No skills added</span>}
          </div>
          <div className="flex gap-2">
            <input type="text" value={newBadge} onChange={(e) => setNewBadge(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBadge())} placeholder="Add skill..."
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500" />
            <button type="button" onClick={addBadge} disabled={!newBadge.trim() || badges.length >= 10}
              className="p-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Languages */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 text-green-600 rounded-lg"><Languages className="w-5 h-5" /></div>
            <h3 className="font-bold text-slate-900">Languages</h3>
          </div>
          <div className="flex flex-wrap gap-2 mb-4 min-h-[40px]">
            {languages.map(lang => (
              <span key={lang} className="pl-3 pr-2 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-bold flex items-center gap-1 border border-green-100">
                {lang}
                <button onClick={() => removeLanguage(lang)} className="hover:text-red-600 p-0.5"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {languages.length === 0 && <span className="text-xs text-slate-400 italic">No languages added</span>}
          </div>
          <div className="flex gap-2">
            <input type="text" value={newLanguage} onChange={(e) => setNewLanguage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLanguage())} placeholder="Add language..."
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500" />
            <button type="button" onClick={addLanguage} disabled={!newLanguage.trim() || languages.length >= 10}
              className="p-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Certifications */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 text-amber-600 rounded-lg"><Award className="w-5 h-5" /></div>
            <h3 className="font-bold text-slate-900">Certifications</h3>
          </div>
          <div className="flex flex-wrap gap-2 mb-4 min-h-[40px]">
            {certifications.map(cert => (
              <span key={cert} className="pl-3 pr-2 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-bold flex items-center gap-1 border border-amber-100">
                {cert}
                <button onClick={() => removeCertification(cert)} className="hover:text-red-600 p-0.5"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {certifications.length === 0 && <span className="text-xs text-slate-400 italic">No certifications added</span>}
          </div>
          <div className="flex gap-2">
            <input type="text" value={newCertification} onChange={(e) => setNewCertification(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())} placeholder="Add certification..."
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500" />
            <button type="button" onClick={addCertification} disabled={!newCertification.trim() || certifications.length >= 10}
              className="p-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Portfolio Items */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-rose-100 text-rose-600 rounded-lg"><ImageIcon className="w-5 h-5" /></div>
          <h3 className="font-bold text-slate-900 text-lg">Featured Work / Portfolio</h3>
        </div>

        {portfolioItems.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {portfolioItems.map((item) => (
              <div key={item.id} className="relative group rounded-xl overflow-hidden border border-slate-200">
                <img src={item.imageUrl} alt={item.title} className="w-full h-32 object-cover" />
                <div className="p-3">
                  <p className="font-bold text-slate-900 text-sm truncate">{item.title}</p>
                  {item.category && <p className="text-xs text-slate-500">{item.category}</p>}
                </div>
                <button type="button" onClick={() => removePortfolioItem(item.id)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border-2 border-dashed border-slate-200 rounded-xl p-6">
          <h4 className="font-bold text-slate-700 text-sm mb-4">Add New Portfolio Item</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Project Title *</label>
              <input type="text" value={newPortfolioItem.title} onChange={(e) => setNewPortfolioItem(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., E-commerce Redesign" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Category</label>
              <input type="text" value={newPortfolioItem.category} onChange={(e) => setNewPortfolioItem(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Web Design" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Project Link</label>
              <input type="url" value={newPortfolioItem.linkUrl} onChange={(e) => setNewPortfolioItem(prev => ({ ...prev, linkUrl: e.target.value }))}
                placeholder="https://example.com/project" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-1">
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Project Image *</label>
              <div onClick={() => portfolioInputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-300 transition-colors">
                {portfolioImagePreview ? (
                  <img src={portfolioImagePreview} alt="Preview" className="max-h-24 mx-auto rounded" />
                ) : (
                  <div className="text-slate-400">
                    <ImageIcon className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-xs">Click to upload image</p>
                  </div>
                )}
              </div>
              <input ref={portfolioInputRef} type="file" accept="image/*" onChange={handlePortfolioImageChange} className="hidden" />
            </div>
            <button type="button" onClick={addPortfolioItem} disabled={!newPortfolioItem.title.trim() || !portfolioImageFile}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mt-6">
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
          {errors.portfolio && <p className="text-red-500 text-xs mt-2">{errors.portfolio}</p>}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-4 sticky bottom-4 z-10">
        <button type="button" onClick={handleSubmit} disabled={isLoading}
          className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 flex items-center gap-2 disabled:opacity-50">
          {isLoading ? (<><Loader2 className="w-5 h-5 animate-spin" />Saving...</>) : (<><Save className="w-5 h-5" />Save Profile</>)}
        </button>
      </div>
    </div>
  );
};

export default ProviderSettings;